import {
  CalendarDayFri,
  CalendarDayMon,
  CalendarDaySat,
  CalendarDaySun,
  CalendarDayThu,
  CalendarDayTue,
  CalendarDayWed,
} from '@berlitz/icons'
import Spacer from '@berlitz/spacer'
import ArrowLeft1 from '@berlitz/streamline-icons/lib/bold/52-Arrows-Diagrams/01-Arrows/ArrowLeft1'
import ArrowRight1 from '@berlitz/streamline-icons/lib/bold/52-Arrows-Diagrams/01-Arrows/ArrowRight1'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import enGB from 'date-fns/locale/en-GB'
import get from 'lodash/get'
import * as Moment from 'moment'
import React, { useState } from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { Calendar, Event, momentLocalizer, NavigateAction, ToolbarProps } from 'react-big-calendar'

import Datepicker from '@components/Datepicker'
import HR from '@components/HR'
import { Header } from '@layouts/Settings/Profile/style'
import BodySpinner from '@components/BodySpinner'
import { IPTimeGrid } from './interface'
import {
  Wrapper,
  TimeGridContainer,
  SubHeader,
  ToolbarWrapper,
  DateWrapper,
  DateLabel,
  LoaderWrapper,
  ShowHoursContainer,
} from './style'
import ShowHoursButton from '@components/ShowHoursButton'
import RoleBasedView from '@components/RoleBasedView'
import ProfileClock from '@components/ProfileClock'
import { extendMoment } from 'moment-range'
import { convertDateTimeToDate, getMinMaxTime } from './utils'
import Button from '@berlitz/button'
import { FormattedMessage } from 'react-intl'
import { canCreateAvailability } from '@components/InstructorScheduler/util'
import PortalInstructorAvailability from '@classes/InstructorAvailability'
import PortalInstructorUnAvailability from '@classes/InstructorUnAvailability'
import useTimeFormat from '@hooks/useTimeFormat'

const moment = extendMoment(Moment)

moment.locale('en', {
  week: {
    dow: 1,
    doy: 1,
  },
})

const localizer = momentLocalizer(moment)

export const CustomWeekHeader = (header) => {
  // __DEV__ && console.log('header: %o', header)
  const date = moment(header.date)
  const isToday = date.isSame(moment(), 'day')
  const calIcons = {
    0: <CalendarDayMon />,
    1: <CalendarDayTue />,
    2: <CalendarDayWed />,
    3: <CalendarDayThu />,
    4: <CalendarDayFri />,
    5: <CalendarDaySat />,
    6: <CalendarDaySun />,
  }

  return (
    <DateWrapper className={isToday ? 'is-today' : ''}>
      <DateLabel>
        <div className="date">{date.format('DD')}</div>
        <div className="day">{date.format('ddd')}</div>
      </DateLabel>

      {get(calIcons, parseInt(date.format('e'), 10), '')}
    </DateWrapper>
  )
}

export const CustomTimeSlot = (event) => {
  if (moment(event.value).format('mm') === '15' || moment(event.value).format('mm') === '45') {
    return <div className="rbc-label rbc-label-empty"></div>
  }
  return moment(event.value).format('mm') === '15' || moment(event.value).format('mm') === '45' ? <></> : event.children
}

const CustomToolbar = (
  toolbar: ToolbarProps,
  customHeader,
  showAll,
  setShowAll,
  isAdmin,
  setCurrentDate,
  onBulkDeleteClick
) => {
  const currentDate = moment(toolbar.date)
  const onNavigate = (action: NavigateAction, date: Date) => {
    let selectedDate = moment(date).clone().toDate()

    if (action === 'DATE') {
      selectedDate = date
      setCurrentDate(moment(selectedDate).clone())
    }

    toolbar.onNavigate(action, selectedDate)

    if (action === 'NEXT' || action === 'PREV') {
      setCurrentDate(moment(selectedDate).clone()[action === 'NEXT' ? 'add' : 'subtract'](1, 'week').toDate())
    }
  }

  return (
    <>
      <Header>
        <SubHeader>
          <div className="holder">
            <div>{customHeader ? customHeader(toolbar) : <div />}</div>
            <div>
              <div className="spacer">
                <Spacer size="md" />
              </div>
              <ProfileClock hideChangeLink={isAdmin} />
            </div>
          </div>

          <div className="holder">
            <div className="action-btns">
              <div>
                <RoleBasedView configId="c::instructor::availability::showall">
                  <ShowHoursContainer>
                    <ShowHoursButton showAll={showAll} setShowAll={setShowAll} customLabel="Show available hours" />
                  </ShowHoursContainer>
                </RoleBasedView>
              </div>
              <div>
                <Button className="bulkdelete-btn" buttonType="error" onClick={onBulkDeleteClick}>
                  <FormattedMessage id="Bulk delete" />
                </Button>
              </div>
            </div>
            <div>
              <ToolbarWrapper>
                <div className="icon" onClick={() => onNavigate('PREV', toolbar.date)}>
                  <ArrowLeft1 />
                </div>
                <div className="date-wrapper">
                  <Datepicker
                    value={currentDate.clone().format('DD/MM/YYYY')}
                    dateFormat="dd/MM/yyyy"
                    openToDate={currentDate.clone().toDate()}
                    onChange={(date) => onNavigate('DATE', date)}
                    isClearable={false}
                    popperPlacement="bottom-end"
                    popperModifiers={{
                      flip: { behavior: ['bottom'] },
                      preventOverflow: { enabled: false },
                      hide: { enabled: false },
                    }}
                    locale={enGB}
                  />
                </div>
                <div className="icon" onClick={() => onNavigate('NEXT', toolbar.date)}>
                  <ArrowRight1 />
                </div>
              </ToolbarWrapper>
            </div>
          </div>
        </SubHeader>
      </Header>

      <Spacer size="xxxs" />
      <HR disableMargin />
      <Spacer size="xs" />
    </>
  )
}

type Props = WithApolloClient<IPTimeGrid & WithUserTimeProps>

const TimeGrid: React.FC<Props> = ({
  events,
  getUserDate,
  customEvent,
  customHeader,
  onSelectEvent,
  onSelectSlot,
  onNavigate,
  loading,
  isAdmin,
  timezone = moment.tz.guess(),
  onBulkDeleteClick = () => {},
  profile,
}) => {
  const { getTimeFormat } = useTimeFormat()
  const [showAll, setShowAll] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const convertedEvents = events.map(({ start, end, ...rest }) => {
    if (rest.resource?.AvailabilityType && rest.resource?.TimeZone !== timezone) {
      const { StartDate, StartTime, EndTime, TimeZone } = rest.resource
      return {
        start: convertDateTimeToDate(moment.tz(`${StartDate} ${StartTime}`, TimeZone), timezone),
        end: convertDateTimeToDate(moment.tz(`${StartDate} ${EndTime}`, TimeZone), timezone),
        ...rest,
      }
    }

    return {
      start: rest.resource?.AvailabilityType ? start : convertDateTimeToDate(start, timezone),
      end: rest.resource?.AvailabilityType ? end : convertDateTimeToDate(end, timezone),
      ...rest,
    }
  })
  const { min, max } = getMinMaxTime(currentDate, convertedEvents, loading, showAll)
  const EndDateWithBerlitz = profile?.InstructorProfile?.instructorInfo?.EndDateWithBerlitz

  return (
    <Wrapper>
      {loading && (
        <LoaderWrapper>
          <div className="wrapper">
            <BodySpinner />
          </div>
        </LoaderWrapper>
      )}

      <TimeGridContainer>
        <Calendar
          className="availability-calendar"
          selectable
          localizer={localizer}
          events={convertedEvents}
          getNow={() => new Date(getUserDate().format('llll'))}
          eventPropGetter={(event: Event) => {
            const resource: PortalInstructorAvailability | PortalInstructorUnAvailability = event.resource
            return {
              className: `event-slot ${resource.getCalendarEventClassName(getUserDate(), isAdmin)}`,
            }
          }}
          defaultView={'week'}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectSlot}
          onSelecting={({ start }) => canCreateAvailability(EndDateWithBerlitz, start)}
          components={{
            event: customEvent,
            header: CustomWeekHeader,
            timeSlotWrapper: CustomTimeSlot,
            toolbar: (toolbar: ToolbarProps) =>
              CustomToolbar(toolbar, customHeader, showAll, setShowAll, isAdmin, setCurrentDate, onBulkDeleteClick),
          }}
          views={['week']}
          step={15}
          timeslots={1}
          min={min}
          max={max}
          onNavigate={(newDate, view, action) => {
            console.log('Timegrid (Calendar) onNavigate', { newDate, view, action })
            onNavigate && onNavigate(newDate, view, action)
          }}
          formats={{
            timeGutterFormat: getTimeFormat(),
            selectRangeFormat: (range) => {
              const start = moment(range.start).format(getTimeFormat())
              const end = moment(range.end).format(getTimeFormat())

              return `${start} - ${end === '23:59' ? '24:00' : end}`
            },
          }}
          showMultiDayTimes
        />
      </TimeGridContainer>

      <Spacer size="xxxl" />
    </Wrapper>
  )
}

export default withApollo(WithUserTimeHOC(TimeGrid))
